// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  withStyles,
  Typography,
  Grid,
} from '@material-ui/core';
import { includes, some, isArray, get } from 'lodash';
import queryString from 'query-string';
import WarningIcon from "@material-ui/icons/Warning";
import { arrayOf, shape, string, bool, func } from 'prop-types';

import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import {
  AlertMessages,
  ErrorContainer,
  FormTextField,
  FileDropzone,
  FormActionButtons,
} from '../../../../components';
import { getIsProviderNameUnique } from '../../../../reducers/authenticationSettings';
import {
  checkProviderNameUnique,
  setAuthSettings,
  updateAuthScheme,
  saveAuthScheme,
} from '../../../../actions/authenticationSettings';
import { getFileExtension, hasError } from '../../../../utils';
import { ALERT_ERROR } from '../../../../constants';
import Actions from '../Actions';
import styles from '../styles';

export const Details = (props) => {
  const {
    classes,
    errors,
    authDetails,
    uuid,
    notifyMessages,
    isProviderNameUnique,
    onCancelDetails,
    tabValue,
    hasAttributeMappingFields,
    hasProviderConfigFields,
  } = props;

  const intl = getI18n(useIntl());
  const queryParam = queryString.parse(props.location.search);
  const qParam = get(queryParam, 'authType') ? `?authType=${queryParam.authType}` : '';

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [name, setName] = useState('');
  const [isNameError, setIsNameError] = useState(false);
  const [isUniqueNameError, setIsUniqueNameError] = useState(false);
  const [description, setDescription] = useState('');
  const [isDescriptionError, setIsDescriptionError] = useState(false);
  const [isFileValidation, setIsFileValidation] = useState(false);
  const [fileValidationMessage, setFileValidationMessage] = useState('');
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const FILE_TYPE = '.png';
  const FILE_HELPER_TEXT = intl.getI18nMessage('label.upload.icon.help.text', { ext: 'png', size: '500kb' });
  const MAX_FILE_SIZE = 500*1024;
  const ALLOWED_FILE_EXTENSIONS = ['png'];

  const [icon, setIcon] = useState({
    files: [],
    acceptFileTypes: FILE_TYPE,
    maxFiles: 1,
    error: false,
    helperText: FILE_HELPER_TEXT,
    base64: '',
  });

  useEffect(() => {
    setIsUniqueNameError(!isProviderNameUnique);
  }, [isProviderNameUnique, name]);

  useEffect(() => {
    if(tabValue === 'basic-details-tab') {
      setName(get(authDetails, 'name'));
      setDescription(get(authDetails, 'description'));
      if(localStorage.getItem('isUnSavedChanges') === 'true') {
        setIsUnsavedChanges(true);
      } else {
        setIsUnsavedChanges(false);
      }
      icon.files = [];
      const base64 = get(authDetails, 'logo');
        if(base64) {
        const file1 = new File([
          new Blob([base64]),
        ], 'icon');
        icon.files.push(file1);
      }
      setIcon({ ...icon, ...{ base64: base64 } });
    }
  }, [authDetails, tabValue]);

  useEffect(() => {
    if(isUnsavedChanges) {
      const hasSave = !hasAttributeMappingFields(authDetails, get(queryParam, 'authType')) ||
        !hasProviderConfigFields(authDetails, get(queryParam, 'authType')) ||
        hasError(name, true) || hasError(description, true);
      setIsSaveDisabled(hasSave);
    }
  }, [isUnsavedChanges, name, description, icon, tabValue]);

  const clearErrorMessages = () => {
    setIcon({ ...icon, error: false });
    setIsFileValidation(false);
    notifyMessages('', '');
  }

  const hasValidateFile = (file) => {
    const extension = getFileExtension(file.name, ALLOWED_FILE_EXTENSIONS);
    if (!includes(ALLOWED_FILE_EXTENSIONS, extension)) {
      icon.error = true;
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      icon.error = true;
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.upload.file.size'));
      return false;
    }
    if (some(icon.files, { name: file.name })) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.name.unique'));
      return false;
    }
    if (icon.files.length === 1) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.auth.file.count'));
      return false;
    }
    setIsFileValidation(false);
    return true;
  };

  const handleChange = (fieldName, value) => {
    if(fieldName === 'name') {
      const nameUiError = hasError(value, true);
      setName(value);
      setIsNameError(nameUiError);
    } else if(fieldName === 'description') {
      setDescription(value);
      setIsDescriptionError(hasError(value, true));
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  }

  const onCheckProviderNameUnique = async (name, uuid) => {
    await props.checkProviderNameUnique(name, uuid);
  };

  const handleBlur = (value) => {
    if (value) {
      onCheckProviderNameUnique(value, uuid);
    } else {
      setIsNameError(true);
    }
  }

  const fileLoad = (file) => {
    const reader = new FileReader();
    reader.onabort = () => {
      icon.error = true;
      setIcon({ ...icon });
    };
    reader.onerror = () => {
      icon.error = true;
      setIcon({ ...icon });
    };
    reader.onload = (e) => {
      if (hasValidateFile(file)) {
        icon.error = false;
        const fileContent = reader.result;
        const fileExtenstion = getFileExtension(file.name, ALLOWED_FILE_EXTENSIONS);
        const fileData = file;
        fileData.ext = fileExtenstion;
        fileData.blob = new Blob(
          [fileContent],
          { type: FILE_TYPE },
        );
        icon.base64 = e.target.result;
        icon.files.push(fileData);
        setIcon({ ...icon });
      } else {
        setIcon({ ...icon });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFiles = (files) => {
    if (isArray(files)) {
      files.forEach((file) => {
        fileLoad(file);
      });
    } else {
      fileLoad(files);
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  };

  const onRemoveFile = (file) => {
    clearErrorMessages();
    const remainFiles = icon.files.filter((obj) => obj.name !== file.name);
    setIcon({ ...icon, ...{ base64: '', files: remainFiles } });

    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  };

  const assistText = get(queryParam, 'authType') === 'LDAP' && uuid ? (<span>
    <WarningIcon className={classes.warningIcon} />
    {getI18nFormattedMessage('label.auth.ldap.assist.text')}
    </span>) : '';

  const onNext = () => {
    const nameUiError = hasError(name, true);
    const descriptionUiError = hasError(description, true);
    if(nameUiError || descriptionUiError) {
      setIsNameError(nameUiError);
      setIsDescriptionError(descriptionUiError);
      if (isUniqueNameError) {
        setNotificationStatus(ALERT_ERROR);
        setNotificationMessage(intl.getI18nMessage('error.api.field.unique.name'));
      }
    } else {
      setNotificationStatus('');
      setNotificationMessage('');
      setIsUnsavedChanges(false);
      setIsFileValidation(false);
      setFileValidationMessage('');
      const imgBase64 = get(icon, 'base64');
      const data = { ...authDetails, ...{ name: name, description: description, logo: imgBase64 } };
      if (get(authDetails, 'authznType') === 'PORTAL') {
        delete data.RoleMappings;
      }
      props.setAuthSettings(data);
      const url = uuid ? `/publish/settings/authentication/edit/${uuid}/provider-configuration${qParam}`
      : `/publish/settings/authentication/add/provider-configuration${qParam}`;
      props.push(url);
    }
  }

  const onSave = () => {
    const imgBase64 = get(icon, 'base64');
    const data = { ...authDetails, ...{ name: name, description: description, logo: imgBase64 } };
    if (get(authDetails, 'authznType') === 'PORTAL') {
      delete data.RoleMappings;
    }
    if (uuid) {
      props.updateAuthScheme(uuid, data);
    } else {
      props.saveAuthScheme(uuid, data);
    }
  }

  const onCancel = () => {
    onCancelDetails(isUnsavedChanges);
  }

  return(
    <div
      id="auth-settings-details-container"
      data-apim-test="auth-settings-details-container"
      className={classes.pageContainer}
    >
      {notificationMessage &&
        <AlertMessages
          id="auth-settings-details-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container>
        <Grid md={10} item>
          <Typography variant="h1" className={classes.pageTitle}>
            {intl.getI18nMessage('label.auth.basic.details')}
          </Typography>
        </Grid>
        <Grid md={2} item>
          <div className={classes.deleteContainer}>
            <Actions
              {...props}
              uuid={uuid}
              notifyMessages={notifyMessages}
              disabled={get(authDetails, 'defaultConfig') }
            />
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
          <FormTextField
            id={'auth-settings-details-name'}
            name={intl.getI18nMessage('label.auth.provider.name')}
            value={name}
            error={isNameError || isUniqueNameError}
            handleChange={(value) => handleChange('name', value, 255)}
            handleBlur={(value) => handleBlur(value)}
            helperText={`${intl.getI18nMessage('label.maximum.length.255')}
              ${intl.getI18nMessage('label.name.unique')}`}
          />
          <FormTextField
            id={'auth-settings-details-description'}
            name={intl.getI18nMessage('label.description')}
            value={description}
            error={isDescriptionError}
            handleChange={(value) => handleChange('description', value)}
            helperText={intl.getI18nMessage('label.maximum.length.255')}
            multiline
            rows={4}
          />
          <FileDropzone
            handleFiles={handleFiles}
            selectedFiles={icon}
            labelClass={classes.fileLabel}
            title={intl.getI18nMessage('label.upload.icon')}
            id={'auth-settings-details-icon-upload'}
            allowedFileExtentions={ALLOWED_FILE_EXTENSIONS}
            onRemoveFile={onRemoveFile}
            isValidation={isFileValidation}
            validationMessage={fileValidationMessage}
          />
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={getI18nFormattedMessage('label.next.button')}
        onCancelClick={onCancel}
        showAdditional={true}
        onAdditionalClick={onSave}
        isDisabledAdditional={isSaveDisabled}
        assistText={assistText}
        id="auth-settings-details-buttons"
      />
    </div>
  )
}

Details.propTypes = {
  classes: shape({}),
  errors: arrayOf({}),
  assets: shape({}),
  location: shape({}),
  uuid: string,
  notifyMessages: func,
  isProviderNameUnique: bool,
  checkProviderNameUnique: func,
  authDetails: shape({}),
  setAuthSettings: func,
  onCancelDetails: func,
  setStateTabs: func,
  tabValue: string,
  saveAuthScheme: func,
  updateAuthScheme: func,
  push: func,
  hasAttributeMappingFields: func,
  hasProviderConfigFields: func,
};

const mapStateToProps = (state) => ({
  isProviderNameUnique: getIsProviderNameUnique(state),
});

const mapDispatchToProps = {
  checkProviderNameUnique,
  setAuthSettings,
  saveAuthScheme,
  updateAuthScheme,
};

export default compose(
withStyles(styles),
connect(mapStateToProps, mapDispatchToProps),
)(Details);
